import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import { DateTime } from "luxon";
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
} from "rxjs";
import { map, switchMap, takeUntil, tap } from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { IUser } from "src/app/admin/users/models/IUser";
import { ITimePeriod } from "src/app/time-periods/models/ITimePeriod";
import { TimePeriodsService } from "src/app/time-periods/time-periods.service";
import {
  CalendarDay,
  CalendarProject,
  CalendarProjectDay,
} from "src/app/timetracking/models/CalendarCell";
import { ITimeEntry } from "src/app/timetracking/models/ITimeEntry";
import { TimetrackingService } from "src/app/timetracking/timetracking.service";
import { ProjectService } from "src/app/admin/shared/services/project.service";

export enum TimeCalendarView {
  Day = "day",
  Week = "week",
  Month = "month",
}
export enum TimeCalendarNavigation {
  Prev = "minus",
  Today = "today",
  Next = "plus",
}

@Component({
  selector: "app-time-calendar",
  templateUrl: "./time-calendar.component.html",
  styleUrls: ["./time-calendar.component.scss"],
})
export class TimeCalendarComponent implements OnInit, OnDestroy {
  @Input() user$: Observable<IUser>;
  @Input() view$ = new BehaviorSubject<TimeCalendarView>(TimeCalendarView.Week);
  @Input() navigation$?: Observable<TimeCalendarNavigation>;

  @Output() viewDay = new EventEmitter<{ project: IProject; day: DateTime }>();
  @Output() addEntry = new EventEmitter<{ project: IProject; day: DateTime }>();

  private destroy = new Subject();

  private currentView: TimeCalendarView = TimeCalendarView.Week;

  dateRange: CalendarDay[] = [];
  timePeriods: ITimePeriod[] = [];
  projects: CalendarProject[] = [];
  totals: number[] = [];

  // used to only allow one quick edit at a time
  isEditing = true;

  get grandTotal(): number {
    // TODO i might want to set these once instead of calculate it everytime we access it
    return this.totals.reduce((total, minutes) => total + minutes, 0);
  }
  // TODO how to set max height so that it doesnt go off the bottom of the page

  constructor(
    private timeTrackingService: TimetrackingService,
    private projectService: ProjectService,
    private timePeriodService: TimePeriodsService
  ) {}

  ngOnInit() {
    const viewChange$ = this.view$.pipe(
      tap((view) => {
        this.currentView = view;
        this.dateRange = this.getDates(view);
      }),
      map(() => this.dateRange)
    );
    const frameChange$ = (this.navigation$ || of()).pipe(
      tap((nav) => {
        this.dateRange = this.moveDateRange(nav, this.currentView);
      }),
      map(() => this.dateRange)
    );
    // when the date changes we need to get new time period and see if they are locked or not
    const dateChange$ = merge(viewChange$, frameChange$).pipe(
      tap(() => (this.isEditing = false)),
      switchMap((dateRange) =>
        this.timePeriodService.getTimePeriodExceptions(
          dateRange[0].date,
          dateRange[dateRange.length - 1].date
        )
      ),
      map((timePeriods) => {
        this.timePeriods = timePeriods;
        return this.dateRange;
      })
    );
    const dataChange$ = this.timeTrackingService.timeChanges$.pipe(
      map(() => this.dateRange)
    );
    const dateRange$ = merge(dateChange$, dataChange$);

    combineLatest([dateRange$, this.user$])
      .pipe(
        takeUntil(this.destroy),
        tap(([dateRange, user]) => {
          // unlocked: !locked
          // locked: locked && !hasException
          // exception: locked && hasException
          dateRange.forEach((calendarDay) => {
            const timePeriod = this.timePeriods.find((period) => {
              return (
                calendarDay.date.startOf("month").toMillis() ===
                period.date.startOf("month").toMillis()
              );
            });
            const exceptions = timePeriod
              ? timePeriod.timePeriodExceptions || []
              : [];
            calendarDay.isLocked = timePeriod && timePeriod.locked;
            // we cant do this here if we dont know that we have userid
            calendarDay.hasException = !!exceptions.find((e) => {
              return e.userId === user.id;
            });
          });
        }),
        switchMap(([dateRange, user]) => {
          const dateKey = "month";
          const start = dateRange[0].date.startOf(dateKey);
          const end = dateRange[dateRange.length - 1].date.endOf(dateKey);
          return combineLatest([
            this.projectService.getProjectsWorkedInRange(user.id, start, end),
            this.getTimeEntries(dateRange, user),
          ]);
        })
      )
      .subscribe(([recentProjects, timeEntries]) => {
        const [projects, totals] = this.groupTimeEntries(
          recentProjects,
          this.dateRange,
          timeEntries
        );
        this.projects = projects;
        this.totals = totals;
      });
  }
  ngOnDestroy() {
    this.destroy.next();
  }
  viewEntriesForDay({ project, day }: { project: IProject; day: DateTime }) {
    this.viewDay.emit({ project, day });
  }
  addEntryForDay({ project, day }: { project: IProject; day: DateTime }) {
    if (this.timeTrackingService.canAddOnDay(day)) {
      this.addEntry.emit({ project, day });
    } else {
      // TODO show an alert modal
      alert("cannot add entries for the future");
    }
  }
  quickEditStart() {
    this.isEditing = true;
  }
  quickEditEnd() {
    this.isEditing = false;
  }
  private getDates(view: TimeCalendarView): CalendarDay[] {
    let start = DateTime.local().startOf(view);
    let end = start.endOf(view);
    if (view == TimeCalendarView.Week) {
      ({ start, end } = this.getSundayWeek(DateTime.local()));
    }
    return this.generateDateRange(start, end);
  }
  private getSundayWeek(date: DateTime) {
    const offset = date.weekday % 7;
    const start = date.minus({ days: offset }).startOf("day");
    const end = start.plus({ days: 6 }).endOf("day");
    return { start, end };
  }
  private moveDateRange(
    nav: TimeCalendarNavigation,
    view: TimeCalendarView
  ): CalendarDay[] {
    const currentStart = this.dateRange[0].date;
    let newStart: DateTime;
    if (nav === TimeCalendarNavigation.Today) {
      newStart = DateTime.local();
      if (this.dateRange.length > 7) 
        newStart = newStart.minus((newStart.day - 1) * 24 * 60 * 60 * 1000);
      else if (this.dateRange.length === 7)
        newStart = newStart.minus((newStart.weekday - 1) * 24 * 60 * 60 * 1000);
    } else {
      newStart = currentStart[nav]({ [view]: 1 });
    }
    var newEnd = newStart.endOf(view);
    if (view == TimeCalendarView.Week) {
      if (nav == TimeCalendarNavigation.Today) {
        //minus 1 day for Sun-Sat, instead of Mon-Sun
        newStart = newStart.minus(60 * 60 * 24 * 1000);
        newEnd = (newStart.endOf(view).minus(-(60 * 60 * 24 * 1000))).endOf(view).minus(60 * 60 * 24 * 1000);
      }
      else {
        //add 1 day, create a new date using EndOf(Week) and then minus 1 day so it will be always 7 days (Sun-Sat)
        newEnd = (newEnd.minus(-(60 * 60 * 24 * 1000))).endOf(view).minus(60 * 60 * 24 * 1000);
      }
    }
    return this.generateDateRange(newStart, newEnd);
  }
  private generateDateRange(start: DateTime, end: DateTime): CalendarDay[] {
    const dates: CalendarDay[] = [];
    let current = start;
    while (current <= end) {
      const isLocked = false;
      dates.push(new CalendarDay(current, isLocked));
      current = current.plus({ days: 1 });
    }
    return dates;
  }
  private getTimeEntries(dates: CalendarDay[], user: IUser) {
    return this.timeTrackingService.getUserHoursInPeriod(
      user.id,
      dates[0].date,
      dates[dates.length - 1].date
    );
  }

  private groupTimeEntries(
    projects: IProject[],
    dates: CalendarDay[],
    timeEntries: ITimeEntry[]
  ): [CalendarProject[], number[]] {
    const totals = dates.map(() => 0);
    const groupedByProject: CalendarProject[] = projects.map(
      (p) => new CalendarProject(p, dates)
    );

    timeEntries.forEach((entry) => {
      const workDay = entry.workDay.startOf("day");
      // this is for the totals row
      const dateIndex = dates.findIndex(
        (day) => day.date.startOf("day").toMillis() === workDay.toMillis()
      );
      totals[dateIndex] += entry.timeSpentMinutes;

      let projectGroup = groupedByProject.find(
        (group) => group.project.id === entry.projectId
      );
      if (!projectGroup) {
        console.log(
          "This time entry belongs to a project that wasnt returned by getting recent projects",
          entry,
          projects
        );
        projectGroup = new CalendarProject(entry.project, dates);
        groupedByProject.push(projectGroup);
      }
      let projectGroupDay = projectGroup.days.find(
        (day) => day.date.startOf("day").toMillis() === workDay.toMillis()
      );
      // TODO should never happen, creating calendarproject will create days.. if this happens there is something wrong
      if (!projectGroupDay) {
        console.error("couldnt find day in project group", workDay);
      }
      if (projectGroupDay) {
        projectGroupDay.timeEntries.push(entry);
      }
    });
    console.log("want to sort these", groupedByProject);
    groupedByProject.sort(this.sortByProjectName);
    return [groupedByProject, totals];
  }
  private sortByProjectName(a: CalendarProject, b: CalendarProject) {
    if (a.project.name < b.project.name) {
      return -1;
    }
    if (a.project.name > b.project.name) {
      return 1;
    }
    return 0;
  }
}
